import { useState, useCallback } from 'react'
import { Copy, RefreshCw, Check } from 'lucide-react'
import {
  Button,
  Card,
  Checkbox,
  IconButton,
  PageHeader,
} from '@/components/ui'
import { cn } from '@/lib/utils.ts'

const CHARSET = {
  upper: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  lower: 'abcdefghijklmnopqrstuvwxyz',
  numbers: '0123456789',
  special: '!@#$%^&*()-_=+[]{}|;:,.<>?',
}

const PRESETS = [
  { label: 'Standard', length: 16, upper: true, lower: true, numbers: true, special: true },
  { label: 'WLAN-Passwort', length: 20, upper: true, lower: true, numbers: true, special: true },
  { label: 'PIN (6-stellig)', length: 6, upper: false, lower: false, numbers: true, special: false },
  { label: 'Einfach (ohne Sonderz.)', length: 12, upper: true, lower: true, numbers: true, special: false },
]

function generatePassword(
  length: number,
  opts: { upper: boolean; lower: boolean; numbers: boolean; special: boolean },
): string {
  let charset = ''
  if (opts.upper) charset += CHARSET.upper
  if (opts.lower) charset += CHARSET.lower
  if (opts.numbers) charset += CHARSET.numbers
  if (opts.special) charset += CHARSET.special
  if (!charset) return ''
  const arr = new Uint8Array(length)
  crypto.getRandomValues(arr)
  return Array.from(arr).map((b) => charset[b % charset.length]).join('')
}

function getStrength(pw: string): { label: string; barColor: string; textColor: string; width: string } {
  if (pw.length < 8) return { label: 'Schwach', barColor: 'bg-red-500', textColor: 'text-red-600', width: 'w-1/4' }
  if (pw.length < 12) return { label: 'Mittel', barColor: 'bg-amber-500', textColor: 'text-amber-600', width: 'w-1/2' }
  if (pw.length < 16) return { label: 'Stark', barColor: 'bg-blue-500', textColor: 'text-blue-600', width: 'w-3/4' }
  return { label: 'Sehr stark', barColor: 'bg-green-500', textColor: 'text-green-600', width: 'w-full' }
}

export function PasswordGeneratorPage() {
  const [length, setLength] = useState(16)
  const [opts, setOpts] = useState({ upper: true, lower: true, numbers: true, special: true })
  const [password, setPassword] = useState(() =>
    generatePassword(16, { upper: true, lower: true, numbers: true, special: true }),
  )
  const [copied, setCopied] = useState(false)

  const generate = useCallback(() => {
    setPassword(generatePassword(length, opts))
  }, [length, opts])

  const copy = async () => {
    await navigator.clipboard.writeText(password)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const strength = getStrength(password)

  return (
    <div className="max-w-lg space-y-6">
      <PageHeader title="Passwort-Generator" />

      <Card>
        <Card.Header>
          <Card.Title>Presets</Card.Title>
        </Card.Header>
        <Card.Body className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {PRESETS.map((preset) => (
              <Button
                key={preset.label}
                variant="secondary"
                size="sm"
                className="justify-start"
                onClick={() => {
                  setLength(preset.length)
                  setOpts({
                    upper: preset.upper,
                    lower: preset.lower,
                    numbers: preset.numbers,
                    special: preset.special,
                  })
                  setPassword(generatePassword(preset.length, preset))
                }}
              >
                {preset.label}
              </Button>
            ))}
          </div>

          <div>
            <label className="flex items-center justify-between text-small font-medium text-gray-700 mb-2">
              <span>Länge</span>
              <span className="font-mono text-brand-700">{length}</span>
            </label>
            <input
              type="range"
              min={4}
              max={64}
              value={length}
              onChange={(e) => setLength(Number(e.target.value))}
              className="w-full accent-brand-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            {Object.entries({
              upper: 'Großbuchstaben',
              lower: 'Kleinbuchstaben',
              numbers: 'Zahlen',
              special: 'Sonderzeichen',
            }).map(([key, label]) => (
              <Checkbox
                key={key}
                label={label}
                checked={opts[key as keyof typeof opts]}
                onChange={(e) => setOpts((o) => ({ ...o, [key]: e.target.checked }))}
              />
            ))}
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body className="space-y-3">
          <div className="flex items-center gap-2 bg-gray-50 rounded-md p-3 font-mono text-small break-all">
            <span className="flex-1 text-gray-800">{password || '—'}</span>
            <IconButton
              icon={copied ? <Check className="text-green-600" /> : <Copy />}
              aria-label={copied ? 'Kopiert' : 'Passwort kopieren'}
              variant="ghost"
              size="sm"
              onClick={copy}
            />
          </div>

          <div>
            <div className="flex justify-between text-caption text-gray-500 mb-1">
              <span>Stärke</span>
              <span className={cn('font-medium', strength.textColor)}>{strength.label}</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className={cn('h-full rounded-full transition-all', strength.barColor, strength.width)} />
            </div>
          </div>

          <Button
            variant="primary"
            fullWidth
            leadingIcon={<RefreshCw className="w-4 h-4" />}
            onClick={generate}
          >
            Generieren
          </Button>
        </Card.Body>
      </Card>
    </div>
  )
}
